/**
 * ADR-0147 — the shared per-column Domain override control.
 *
 * One editable Domain cell used by every schema-contract surface (shared
 * provider editor, in-op provider/plex inspector, transform Output panel):
 * a compact pillSelect offering Auto / Date / Number / String / Boolean.
 * "Auto" = no override (source-declared or inferred type, shown beside it);
 * an explicit choice re-types the cached result immediately and re-applies
 * on every run. The raw source Type stays visible in its own column so
 * "source says text, I've declared date" stays legible.
 *
 * Validate-before-convert: a type change that doesn't fit the data is rejected
 * server-side; the caller toasts `typeValidationMessage(...)` and reverts the
 * control. No inline "left blank" hint — nothing is ever null-punched.
 *
 * Pure logic (labels, toast copy, map arithmetic) lives in
 * `lib/columnDomain.ts` for unit-testing.
 */

import {
  COLUMN_DOMAIN_OPTIONS,
  autoOptionLabel,
  domainSelectValue,
  type ColumnDomain,
} from "../lib/columnDomain.js";

export type { ColumnDomain };

export function ColumnDomainSelect(props: {
  columnName: string;
  /** The column's current effective Atlas domain (what Auto resolves to when no override is set). */
  resolvedDomain: string;
  override: string | undefined;
  disabled?: boolean;
  onChange: (columnName: string, domain: ColumnDomain | null) => void;
}) {
  return (
    <select
      className="pillSelect pillSelectNeutral"
      value={domainSelectValue(props.override)}
      disabled={props.disabled}
      title="Column type — Auto keeps the source-declared or detected type; an explicit choice overrides it on every run"
      onChange={(e) =>
        props.onChange(
          props.columnName,
          e.target.value === "auto" ? null : (e.target.value as ColumnDomain)
        )
      }
    >
      <option value="auto">{autoOptionLabel(props.override, props.resolvedDomain)}</option>
      {COLUMN_DOMAIN_OPTIONS.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}
